import { IShipment } from '../types/shipments.types';

const cache: any = { shipments: undefined };
let prevPointer: any = null;
const SHIPMENT_ORDER = {
  QCOMM: 1,
  QELEC: 2,
  EXPRESS: 3,
  CNC: 4,
  STANDARD_FOOD: 5,
  STANDARD_NONFOOD: 6,
  MARKETPLACE: 7,
};

function shipmentHelperFactory(
  shipments: IShipment[] = []
): {
  cnc: any;
  marketPlace: any;
  standardFood: any;
  standardNonFood: any;
  qComm: any;
  qelec: any;
  express: any;
  isFoodCart: boolean;
  isNonFoodCart: boolean;
  allProducts: any[];
} {
  if (Object.is(prevPointer, shipments)) {
    return (
      cache.shipments || {
        qComm: { entries: [] },
        qelec: { entries: [] },
        express: { entries: [] },
        cnc: { entries: [] },
        marketPlace: { entries: [] },
        standardFood: { entries: [] },
        standardNonFood: { entries: [] },
        allProducts: [],
      }
    );
  }
  prevPointer = shipments.sort((s1: IShipment, s2: IShipment) => {
    const s1Order = SHIPMENT_ORDER[s1.shipmentType];
    const s2Order = SHIPMENT_ORDER[s2.shipmentType];

    if (s1Order > s2Order) {
      return 1;
    } else if (s1Order < s2Order) {
      return -1;
    }
    return 0;
  });

  const cncSymbol = Symbol('cnc');
  const qCommSymbol = Symbol('qComm');
  const qelecSymbol = Symbol('qelec');
  const expressSymbol = Symbol('express');
  const marketplaceSymbol = Symbol('marketPlace');
  const standardFoodSymbol = Symbol('standardFood');
  const standardNonFoodSymbol = Symbol('standardNonFood');

  const typeSymbolMappings: { [key: string]: symbol } = {
    QCOMM: qCommSymbol,
    QELEC: qelecSymbol,
    EXPRESS: expressSymbol,
    CNC: cncSymbol,
    MARKETPLACE: marketplaceSymbol,
    STANDARD_FOOD: standardFoodSymbol,
    STANDARD_NONFOOD: standardNonFoodSymbol,
  };

  const shipmentHelper: any = {
    [qCommSymbol]: { entries: [] },
    [qelecSymbol]: { entries: [] },
    [expressSymbol]: { entries: [] },
    [cncSymbol]: { entries: [] },
    [marketplaceSymbol]: { entries: [] },
    [standardFoodSymbol]: { entries: [] },
    [standardNonFoodSymbol]: { entries: [] },

    // getters for internal properties.. to explicitly prevent any two way writes
    get qComm() {
      return this[qCommSymbol];
    },
    get qelec() {
      return this[qelecSymbol];
    },
    get express() {
      return this[expressSymbol];
    },
    get cnc() {
      return this[cncSymbol];
    },
    get marketPlace() {
      return this[marketplaceSymbol];
    },
    get standardFood() {
      return this[standardFoodSymbol];
    },
    get standardNonFood() {
      return this[standardNonFoodSymbol];
    },
    get isFoodCart() {
      const isNonFoodListEmpty = this[standardNonFoodSymbol].entries.length === 0;
      const isMPListEmpty = this[marketplaceSymbol].entries.length === 0;
      return isNonFoodListEmpty && isMPListEmpty;
    },
    get isNonFoodCart() {
      const isCnCListEmpty = this[cncSymbol].entries.length === 0;
      const isStandardFoodListEmpty = this[standardFoodSymbol].entries.length === 0;
      const isQCommListEmpty = this[qCommSymbol].entries.length === 0;
      const isQelecListEmpty = this[qelecSymbol].entries.length === 0;
      const isExpressListEmpty = this[expressSymbol].entries.length === 0;

      return (
        isCnCListEmpty &&
        isStandardFoodListEmpty &&
        isExpressListEmpty &&
        isQCommListEmpty &&
        isQelecListEmpty
      );
    },
    get allProducts() {
      return [
        ...this.qComm.entries,
        ...this.qelec.entries,
        ...this.express.entries,
        ...this.cnc.entries,
        ...this.marketPlace.entries,
        ...this.standardFood.entries,
        ...this.standardNonFood.entries,
      ];
    },
  };

  const targetShipment = shipments || [];
  let currentShipmentCount = 0;
  targetShipment.forEach((shipment: IShipment, index) => {
    currentShipmentCount += 1;
    const { entries, ...everythingApartFromEntries } = shipment;
    const mkpEntries = shipment.sellersEntries
      ? shipment.sellersEntries.flatMap(sellerEntry =>
          sellerEntry.entries.map(productEntry => productEntry)
        )
      : [];
    shipmentHelper[typeSymbolMappings[shipment.shipmentType]] = {
      ...shipmentHelper[typeSymbolMappings[shipment.shipmentType]],
      ...everythingApartFromEntries,

      entries: entries ? [...entries] : [...mkpEntries],
      currentShipmentCount,
    };
  });
  cache.shipments = shipmentHelper;
  return shipmentHelper;
}

export { shipmentHelperFactory };
